import { Ban, CheckCircle2, FolderTree, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { GoalSubSectorModal } from '../components/GoalSubSectorModal';
import { goalSubSectorsApi } from '../budgetGoalsApi';
import type { GoalSubSectorListItem } from '../budgetGoalTypes';

type TabKey = 'info';

/** One goal sub-sector, on the header layout like every other catalog row here. */
export function GoalSubSectorProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [subSector, setSubSector] = useState<GoalSubSectorListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      goalSubSectorsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setSubSector(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this sub-sector.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await goalSubSectorsApi.activate(id);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore this sub-sector.', 0),
      );
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Investment Planning',
    { label: 'Settings', to: '/procurement/investment-planning/settings' },
    { label: 'Sub-sectors', to: '/procurement/investment-planning/settings/goal-sub-sectors' },
    subSector?.name ?? 'Sub-sector',
  ];

  if (error && !subSector) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (loading || !subSector) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const { goalCount } = subSector;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="goal-sub-sector-profile"
        header={
          <ProfileHeader
            id="goal-sub-sector-profile-header"
            icon={<FolderTree className="h-6 w-6" aria-hidden="true" />}
            title={subSector.name}
            meta={[
              subSector.sectorName,
              `${goalCount} ${goalCount === 1 ? 'goal' : 'goals'}`,
            ]}
            status={
              <StatusText tone={subSector.isActive ? 'active' : 'inactive'}>
                {subSector.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={subSector.createdByName}
            createdAt={subSector.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="goal-sub-sector-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : subSector.isActive ? (
            <ProfileBanner
              id="goal-sub-sector-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This sub-sector is offered: it appears in the Sub-sector picker when a goal is filed, and its goals are offered to a budget plan's Goal Setting."
            />
          ) : (
            <ProfileBanner
              id="goal-sub-sector-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This sub-sector is deleted. Its goals are kept but are no longer offered to a budget plan, and nothing new can be filed here."
              action={
                <PermissionGate permission="budget-goals.delete">
                  <Button
                    id="goal-sub-sector-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="goal-sub-sector-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'budget-goals.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              {
                label: 'Sector',
                /*
                  A LINK to the rung above, because the parent is a record of its own with its
                  own page. `text-slate-800` with an underline on hover — a clickable thing is
                  black here; the row-anchor blue is scoped to a table's click target.
                */
                value: (
                  <Link
                    to={`/procurement/investment-planning/settings/goal-sectors/${subSector.goalSectorId}`}
                    className="font-medium text-slate-800 hover:underline"
                  >
                    {subSector.sectorName}
                  </Link>
                ),
              },
              { label: 'Name', value: subSector.name },
              { label: 'Description', value: subSector.description },
              {
                label: 'Order',
                value: <span className="font-mono tabular-nums">{subSector.sortOrder}</span>,
              },
              {
                label: 'Goals',
                value: (
                  <span className={`font-mono tabular-nums ${goalCount === 0 ? 'text-slate-300' : ''}`}>
                    {goalCount}
                  </span>
                ),
              },
            ]}
          />
        </ProfileCard>

        <ProfileCard id="goal-sub-sector-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: subSector.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(subSector.createdAt)} className="font-mono">
                    {formatDateTime(subSector.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: subSector.updatedByName },
              {
                label: 'Updated',
                value: subSector.updatedAt ? (
                  <time dateTime={toIso(subSector.updatedAt)} className="font-mono">
                    {formatDateTime(subSector.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <GoalSubSectorModal
          open
          editing={subSector}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
